import type { Membership } from '../../../lib/api';

/**
 * Per-tier `monthly_direct_bonus` editor. Each receiver tier stores
 *   config.computation.monthly_direct_bonus = {
 *     direct_distribution_points: { [buyerTier]: points },
 *     direct_minimum_requirements, direct_unilevel_pv_requirements,
 *     mentor_bonus_percentage,
 *   }
 * Top table: receiver × buyer points matrix. Bottom table: the 3 per-tier
 * qualification scalars.
 */
type Block = Record<string, unknown>;

const SCALARS = [
  ['direct_minimum_requirements', 'Min directs'],
  ['direct_unilevel_pv_requirements', 'Min PPV'],
  ['mentor_bonus_percentage', 'Mentor %'],
] as const;

export function MonthlyDirectBonusPerTier({
  memberships,
  draft,
  onChange,
  readOnly,
}: {
  memberships: Membership[];
  draft: Record<string, Record<string, unknown>>;
  onChange: (id: string, next: Record<string, unknown>) => void;
  readOnly: boolean;
}) {
  function block(cfg: Record<string, unknown>): Block {
    const computation = (cfg.computation as Block | undefined) ?? {};
    return (computation.monthly_direct_bonus as Block | undefined) ?? {};
  }

  function writeBlock(id: string, nextBlock: Block) {
    const current = draft[id] ?? {};
    const computation = { ...((current.computation as Block | undefined) ?? {}) };
    const cleaned: Block = {};
    for (const [k, v] of Object.entries(nextBlock)) {
      if (v === undefined) continue;
      if (typeof v === 'object' && v !== null && Object.keys(v as object).length === 0) continue;
      cleaned[k] = v;
    }
    if (Object.keys(cleaned).length === 0) delete computation.monthly_direct_bonus;
    else computation.monthly_direct_bonus = cleaned;
    const next = { ...current };
    if (Object.keys(computation).length === 0) delete next.computation;
    else next.computation = computation;
    onChange(id, next);
  }

  function getPoints(cfg: Record<string, unknown>, buyer: string): number {
    const pts = (block(cfg).direct_distribution_points as Block | undefined) ?? {};
    const v = pts[buyer];
    return typeof v === 'number' || typeof v === 'string' ? Number(v) || 0 : 0;
  }
  function setPoints(id: string, buyer: string, n: number) {
    const b = { ...block(draft[id] ?? {}) };
    const pts = { ...((b.direct_distribution_points as Block | undefined) ?? {}) };
    if (n === 0) delete pts[buyer];
    else pts[buyer] = n;
    b.direct_distribution_points = pts;
    writeBlock(id, b);
  }

  function getScalar(cfg: Record<string, unknown>, key: string): number {
    const v = block(cfg)[key];
    return typeof v === 'number' || typeof v === 'string' ? Number(v) || 0 : 0;
  }
  function setScalar(id: string, key: string, n: number) {
    const b = { ...block(draft[id] ?? {}) };
    if (n === 0) delete b[key];
    else b[key] = n;
    writeBlock(id, b);
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <p className="text-[10px] text-muted-foreground">
          Distribution points per buyer tier. Rows = receiver tier (the earning sponsor); columns =
          buyer tier; cell = points credited per qualifying direct that period.
        </p>
        <div className="overflow-x-auto border rounded-md">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/40 text-xs text-left text-muted-foreground">
                <th className="px-3 py-2 font-medium sticky left-0 bg-muted/40">
                  Receiver \ Buyer
                </th>
                {memberships.map((m) => (
                  <th key={m.code} className="px-3 py-2 font-mono font-medium" title={m.name}>
                    {m.code}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {memberships.map((receiver) => (
                <tr key={receiver.id} className="border-t">
                  <td
                    className="px-3 py-1.5 text-xs font-mono font-medium bg-muted/20 sticky left-0"
                    title={receiver.name}
                  >
                    {receiver.code}
                  </td>
                  {memberships.map((buyer) => (
                    <td key={buyer.code} className="px-2 py-1.5">
                      <input
                        type="number"
                        className="h-8 w-20 px-2 rounded-md border bg-background text-xs disabled:opacity-50"
                        value={getPoints(draft[receiver.id] ?? {}, buyer.code)}
                        disabled={readOnly}
                        step={0.01}
                        min={0}
                        onChange={(e) =>
                          setPoints(receiver.id, buyer.code, Number(e.target.value) || 0)
                        }
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="space-y-2">
        <p className="text-[10px] text-muted-foreground">
          Per-tier qualification thresholds + optional mentor-bonus % that piggybacks on the monthly
          bonus.
        </p>
        <div className="overflow-x-auto border rounded-md">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs text-left text-muted-foreground">
              <tr>
                <th className="px-3 py-2 font-medium">Tier</th>
                {SCALARS.map(([key, label]) => (
                  <th key={key} className="px-3 py-2 font-medium">
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {memberships.map((m) => (
                <tr key={m.id} className="border-t">
                  <td className="px-3 py-1.5 text-xs font-mono">{m.code}</td>
                  {SCALARS.map(([key]) => (
                    <td key={key} className="px-2 py-1.5">
                      <input
                        type="number"
                        className="h-8 w-24 px-2 rounded-md border bg-background text-xs disabled:opacity-50"
                        value={getScalar(draft[m.id] ?? {}, key)}
                        disabled={readOnly}
                        step={0.01}
                        min={0}
                        onChange={(e) => setScalar(m.id, key, Number(e.target.value) || 0)}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
